import { cn, cva, platformProps } from "../../lib";
import { webRole } from "../../lib/web-role";
import { Box } from "../../primitives";
import { Icon } from "../icons";
import { Typography } from "../typography";
import { tagIconSize, tagTextColor, tagTypography } from "./lib";
import type { TagProps } from "./types";

// Both variants keep the 1px border (transparent when filled), so outlined and
// filled Tags are the same size. Fixed heights centre the content.
const tagVariants = cva("rounded-radius-full inline-flex max-w-full flex-row items-center border", {
  variants: {
    // Heights: Figma's `_tags/height`, 20/24/28, from 768px 24/32/40.
    // Side padding and gap: Figma's padding and spacing tokens, picked per
    // breakpoint by value (the spacing classes hold fixed values)
    size: {
      sm: "gap-spacing-xxs px-spacing-sm md:gap-spacing-xs md:px-spacing-md h-tag-sm md:h-tag-sm-dt",
      md: "gap-spacing-xs px-spacing-sm md:gap-spacing-sm md:px-spacing-md h-tag-md md:h-tag-md-dt",
      lg: "gap-spacing-xs px-spacing-md md:gap-spacing-sm md:px-spacing-lg h-tag-lg md:h-tag-lg-dt",
    },
    variant: {
      primary: "bg-luce-brand-primarySurface border-luce-brand-primaryBorder",
      success: "bg-luce-system-success-surface border-luce-system-success-border",
      danger: "bg-luce-system-danger-surface border-luce-system-danger-border",
      info: "bg-luce-system-info-surface border-luce-system-info-border",
      warning: "bg-luce-system-warning-surface border-luce-system-warning-border",
      neutral: "bg-luce-neutral-background-intermediate border-luce-neutral-border-highEmphasis",
    },
    outline: {
      true: "",
      false: "border-transparent",
    },
  },
  defaultVariants: {
    size: "md",
    variant: "neutral",
    outline: false,
  },
});

export const Tag = ({
  children,
  variant = "neutral",
  size = "md",
  outline = false,
  leadingIcon,
  leadingIconColor,
  iconWeight = "regular",
  className,
  accessibilityLabel,
  testID,
}: TagProps) => {
  const textColor = tagTextColor[variant];
  const label = accessibilityLabel || undefined;

  return (
    <Box
      // On web a label needs a group (ARIA gives a role-less div no name); on
      // native the tag becomes one element read by the label, in place of its
      // text. Without one, the visible text is read as-is
      accessibilityRole={label ? webRole("group", "text") : "text"}
      accessibilityLabel={label}
      accessible={label ? true : undefined}
      {...platformProps({ testID, slot: "tag" })}
      className={cn(tagVariants({ size, variant, outline }), className)}
    >
      {leadingIcon && (
        <Icon
          name={leadingIcon}
          size={tagIconSize[size]}
          weight={iconWeight}
          color={leadingIconColor ?? textColor}
          className="shrink-0"
        />
      )}
      {/* One line; a label longer than the space ends in "…", as Chip's does */}
      <Typography
        variant={tagTypography[size]}
        color={textColor}
        whiteSpace="nowrap"
        className="shrink truncate"
      >
        {children}
      </Typography>
    </Box>
  );
};

Tag.displayName = "Tag";
